/**
 * La carrera del jugador: lo que sobrevive al partido.
 *
 * Sin esto no hay nada a lo que volver — el marcador se olvida en cuanto se
 * cierra la pestana. Aqui se acumulan los numeros, sube la media, se desbloquean
 * trofeos, suben las insignias y se lleva la clasificacion de la semana.
 *
 * Todo el calculo es de funciones puras sobre datos: el almacenamiento es la
 * capa fina de fuera. Asi se puede probar sin navegador, y mover a un servidor
 * mas adelante sin tocar las reglas.
 *
 * Reparto de responsabilidades, para no volver a mezclarlo:
 *
 * - `rating.ts` convierte contadores en media, atributos y nivel.
 * - `badges.ts` dice que hay que coleccionar.
 * - Este fichero lleva la cuenta y decide QUE PASA al acabar un partido.
 */
import { weekKeyOf, type TeamSide } from "@golazo/shared";
import {
  BADGES,
  badgeProgress,
  TROPHIES,
  type Badge,
  type TierIndex,
  type Trophy,
} from "./badges.js";
import type { MatchStatsSnapshot, PlayerStat } from "./matchStats.js";
import { playerScore } from "./matchStats.js";
import { levelFor, ratePlayer, xpForMatch } from "./rating.js";

/**
 * Online y offline, por separado (2026-09-26).
 *
 * Contra personas y contra la IA son dos juegos distintos: una media inflada
 * a base de golear bots no dice nada de como juegas contra gente. El online
 * conserva la clave de siempre; el offline estrena la suya. Ver
 * `splitLegacyProfile` para lo que habia antes de separarlos.
 */
export type ProfileSide = "online" | "offline";
const STORAGE_KEYS: Record<ProfileSide, string> = {
  online: "golazo.profile",
  offline: "fynta.profile.offline",
};
const SPLIT_FLAG_KEY = "fynta.profile.split";
const STORAGE_KEY = STORAGE_KEYS.online;
/**
 * Version del formato.
 *
 * La v1 se DESCARTABA al no coincidir, y eso borraba la carrera de quien ya
 * llevaba cien partidos. Ahora se migra: subir de version no puede costarle a
 * nadie lo que tenia.
 */
const PROFILE_VERSION = 2;
/**
 * Partidos que se guardan enteros para el historial.
 *
 * El numero NO es un gusto: sale de `MAX_PROFILE_CHARS`. La carrera viaja al
 * server como una sola cadena JSON, y un `MatchRecord` con la ficha entera pesa
 * unos 274 caracteres. Con 40 la carrera llena daba 14.196 y el server RECHAZA
 * el `profile:push` — en silencio, y justo a quien mas juega.
 *
 * Con 25 son 10.086, un 82 % del tope, y quedan dos kilobytes de margen para
 * los campos que vengan. `pnpm profile:size` lo mide y un test lo vigila: quien
 * añada una columna al historial se entera en `pnpm check`, no en produccion.
 */
const HISTORY_LIMIT = 25;
/** Companeros distintos que se recuerdan. */
const PARTNER_LIMIT = 24;

export interface CareerTotals {
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  goals: number;
  assists: number;
  mvps: number;
  shots: number;
  passes: number;
  passesOk: number;
  recoveries: number;
  cleanSheets: number;
  touches: number;
  /** Balones perdidos ante un rival. No confundir con `losses`, que son partidos. */
  turnovers: number;
  goalsFor: number;
  goalsAgainst: number;
  /** Segundos jugados. */
  seconds: number;
}

/** Lo mejor que ha hecho nunca. Cada uno se bate en un partido concreto. */
export interface CareerRecords {
  goalsInMatch: number;
  assistsInMatch: number;
  recoveriesInMatch: number;
  /** Mejor puntuacion de partido (la columna PTS de la tabla final). */
  ratingInMatch: number;
  /** Mayor diferencia de goles en una victoria. */
  biggestWin: number;
  /** Segundo en que cayo tu gol mas rapido. */
  fastestGoal: number | null;
  bestWeekPoints: number;
  bestDayStreak: number;
  bestOverall: number;
}

export interface WeeklyStanding {
  /** Semana ISO, p. ej. "2026-W34". Al cambiar, la clasificacion arranca de cero. */
  weekKey: string;
  points: number;
  matches: number;
  goals: number;
  assists: number;
  mvps: number;
}

export interface PartnerRecord {
  nickname: string;
  played: number;
  won: number;
  lastAt: number;
}

/**
 * Un partido en el historial, con su ficha entera.
 *
 * Guarda la fila completa y no solo el resultado porque la gracia del historial
 * es poder abrir un partido de hace tres semanas y ver exactamente lo que
 * hiciste. Un `MatchRecord` ocupa ~200 bytes; cuarenta caben de sobra en
 * `localStorage`.
 */
export interface MatchRecord {
  at: number;
  /**
   * Nivel de la IA rival (0 barrio … 1 la de siempre), solo contra la IA. Para
   * poder medir como le va a la gente segun la dificultad. Sin el: partido de
   * antes de que hubiera niveles (la de siempre) u online.
   */
  aiLevel?: number;
  modeId: string;
  online: boolean;
  result: MatchResult;
  goalsFor: number;
  goalsAgainst: number;
  goals: number;
  assists: number;
  mvp: boolean;
  shots: number;
  passes: number;
  passesOk: number;
  recoveries: number;
  turnovers: number;
  touches: number;
  /** Puntuacion del partido, la misma que ordena la tabla final. */
  rating: number;
  shirtNumber: number;
  xp: number;
  /** Media DESPUES de este partido, para dibujar la curva de la carrera. */
  overall: number;
  seconds: number;
}

export interface Profile {
  version: number;
  /**
   * De que lado es. Los perfiles de antes de la separacion no lo llevan: son
   * una mezcla, y `sideOfLegacy` decide a donde van.
   */
  side?: ProfileSide;
  createdAt: number;
  /** Como se llamaba y con que dorsal jugo la ultima vez. */
  identity: { nickname: string; shirtNumber: number };
  /** Dias distintos con al menos un partido, seguidos. */
  dayStreak: number;
  lastPlayedDay: string | null;
  /** Victorias seguidas. Se corta con cualquier no-victoria. */
  winStreak: number;
  bestWinStreak: number;
  xp: number;
  level: number;
  /** Media actual (40-99). Se recalcula al acabar cada partido. */
  overall: number;
  totals: CareerTotals;
  records: CareerRecords;
  weekly: WeeklyStanding;
  partners: PartnerRecord[];
  /** id de trofeo → cuando se desbloqueo. */
  unlocked: Record<string, number>;
  recent: MatchRecord[];
}

export type MatchResult = "win" | "draw" | "loss";

/** Todo lo que un partido aporta a la carrera, ya masticado. */
export interface MatchOutcome {
  modeId: string;
  online: boolean;
  result: MatchResult;
  goalsFor: number;
  goalsAgainst: number;
  durationSeconds: number;
  /** Ficha del jugador humano, o null si no llego a tocar el campo. */
  stat: PlayerStat | null;
  isMvp: boolean;
  cleanSheet: boolean;
  /** Ibamos por detras y acabamos ganando. */
  comeback: boolean;
  /** Cuantos goles llegamos a ir por debajo en el peor momento. */
  maxDeficit: number;
  /** Gol propio en los ultimos 60 s. */
  lateGoal: boolean;
  /** Gol propio en los primeros 30 s. */
  earlyGoal: boolean;
  /** Segundo de tu gol mas rapido, o null si no marcaste. */
  firstGoalSecond: number | null;
  /** Marcaste TU el gol que acabo el partido en muerte subita. */
  goldenGoalWinner: boolean;
  /** Companeros humanos de este partido. */
  teammates: string[];
  /** Nivel de la IA rival, solo contra la IA (ver `MatchRecord.aiLevel`). */
  aiLevel?: number;
}

export function emptyTotals(): CareerTotals {
  return {
    matches: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    goals: 0,
    assists: 0,
    mvps: 0,
    shots: 0,
    passes: 0,
    passesOk: 0,
    recoveries: 0,
    cleanSheets: 0,
    touches: 0,
    turnovers: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    seconds: 0,
  };
}

export function emptyRecords(): CareerRecords {
  return {
    goalsInMatch: 0,
    assistsInMatch: 0,
    recoveriesInMatch: 0,
    ratingInMatch: 0,
    biggestWin: 0,
    fastestGoal: null,
    bestWeekPoints: 0,
    bestDayStreak: 0,
    bestOverall: 0,
  };
}

/*
 * La clave de semana vive en `@golazo/shared`: la comparten la clasificacion de
 * la carrera y la jornada, y dos implementaciones de lo mismo acaban
 * discrepando justo en fin de ano.
 */
export { weekKeyOf };

export function dayKeyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function emptyWeekly(weekKey: string): WeeklyStanding {
  return { weekKey, points: 0, matches: 0, goals: 0, assists: 0, mvps: 0 };
}

export function emptyProfile(now = Date.now()): Profile {
  const totals = emptyTotals();
  return {
    version: PROFILE_VERSION,
    createdAt: now,
    identity: { nickname: "", shirtNumber: 10 },
    dayStreak: 0,
    lastPlayedDay: null,
    winStreak: 0,
    bestWinStreak: 0,
    xp: 0,
    level: 1,
    // Sin partidos la media no es 0: es la de alguien del monton, porque toda
    // la escala esta encajada contra un promedio. Un 0 diria "eres malisimo"
    // cuando lo que pasa es que no has jugado.
    overall: ratePlayer(totals).overall,
    totals,
    records: emptyRecords(),
    weekly: emptyWeekly(weekKeyOf(new Date(now))),
    partners: [],
    unlocked: {},
    recent: [],
  };
}

/**
 * Puntos de la semana. Ganar pesa mas que lucirse: es un juego de equipo.
 *
 * Solo puntuan los partidos online. Los locales cuentan para la carrera y para
 * los trofeos — quien no tenga con quien jugar tiene que poder progresar — pero
 * no para una clasificacion que se compara con otra gente: contra la IA se
 * fabrican los puntos que uno quiera.
 */
export function weeklyPointsFor(outcome: MatchOutcome): number {
  if (!outcome.online) return 0;
  const base = outcome.result === "win" ? 3 : outcome.result === "draw" ? 1 : 0;
  const goals = (outcome.stat?.goals ?? 0) * 2;
  const assists = outcome.stat?.assists ?? 0;
  return base + goals + assists + (outcome.isMvp ? 3 : 0);
}

/**
 * Traduce el final de un partido a lo que la carrera necesita saber.
 *
 * Se apoya solo en `MatchStatsSnapshot`, que ya reconstruye autor y asistencia
 * de cada gol: aqui no se vuelve a deducir nada de la simulacion.
 */
export function summariseMatch(opts: {
  snapshot: MatchStatsSnapshot;
  humanId: string;
  side: TeamSide;
  score: Record<TeamSide, number>;
  modeId: string;
  online: boolean;
  durationSeconds: number;
  /** El partido acabo en muerte subita. */
  goldenGoal?: boolean;
  /** Nivel de la IA rival, solo contra la IA. */
  aiLevel?: number;
}): MatchOutcome {
  const { snapshot, humanId, side, score, durationSeconds } = opts;
  const stat = snapshot.players.find((p) => p.id === humanId) ?? null;
  const rival: TeamSide = side === "home" ? "away" : "home";
  const goalsFor = score[side] ?? 0;
  const goalsAgainst = score[rival] ?? 0;
  const result: MatchResult =
    goalsFor > goalsAgainst ? "win" : goalsFor === goalsAgainst ? "draw" : "loss";

  // Remontada: hubo un momento en que ibamos por detras, y acabamos ganando.
  // Se guarda ademas CUANTO por detras, que es lo que separa una remontada de
  // una resurreccion.
  let mine = 0;
  let theirs = 0;
  let maxDeficit = 0;
  for (const goal of snapshot.goals) {
    if (goal.side === side) mine += 1;
    else theirs += 1;
    maxDeficit = Math.max(maxDeficit, theirs - mine);
  }

  const myGoals = snapshot.goals.filter((g) => g.scorerId === humanId && !g.ownGoal);
  const lateGoal = myGoals.some((g) => durationSeconds - g.atSecond <= 60);
  const earlyGoal = myGoals.some((g) => g.atSecond <= 30);
  const firstGoalSecond = myGoals.length
    ? myGoals.reduce((min, g) => Math.min(min, g.atSecond), Number.POSITIVE_INFINITY)
    : null;

  // Gol de oro: en muerte subita el partido acaba EN el gol, asi que el ultimo
  // de la lista es el que lo cerro. Solo cuenta si lo marcaste tu y ganaste.
  const last = snapshot.goals[snapshot.goals.length - 1];
  const goldenGoalWinner = Boolean(
    opts.goldenGoal && result === "win" && last && !last.ownGoal && last.scorerId === humanId,
  );

  const teammates = snapshot.players
    .filter((p) => p.side === side && p.id !== humanId && p.nickname)
    .map((p) => p.nickname as string);

  return {
    modeId: opts.modeId,
    online: opts.online,
    result,
    goalsFor,
    goalsAgainst,
    durationSeconds,
    stat,
    isMvp: snapshot.mvp?.stat.id === humanId,
    cleanSheet: goalsAgainst === 0,
    comeback: maxDeficit > 0 && result === "win",
    maxDeficit,
    lateGoal,
    earlyGoal,
    firstGoalSecond: Number.isFinite(firstGoalSecond) ? firstGoalSecond : null,
    goldenGoalWinner,
    teammates,
    ...(opts.aiLevel !== undefined && !opts.online ? { aiLevel: opts.aiLevel } : {}),
  };
}

/** Una insignia que ha subido de escalon en este partido. */
export interface BadgePromotion {
  badge: Badge;
  from: TierIndex;
  to: TierIndex;
}

export interface ApplyResult {
  profile: Profile;
  /** Trofeos que este partido acaba de desbloquear, para celebrarlos. */
  unlocked: Trophy[];
  /** Insignias que han subido de escalon. */
  promoted: BadgePromotion[];
  xpGained: number;
  overallBefore: number;
  overallAfter: number;
  levelBefore: number;
  levelAfter: number;
  /** Etiquetas de los records personales batidos en este partido. */
  brokenRecords: string[];
}

/** Aplica un partido a la carrera. Pura: devuelve un perfil nuevo. */
export function applyMatch(base: Profile, outcome: MatchOutcome, now = Date.now()): ApplyResult {
  const profile: Profile = structuredCloneish(base);
  const date = new Date(now);
  const day = dayKeyOf(date);
  const week = weekKeyOf(date);
  const overallBefore = profile.overall;
  const levelBefore = profile.level;
  const tiersBefore = new Map(BADGES.map((b) => [b.id, badgeProgress(b, profile).tier]));

  const s = outcome.stat;
  if (s) profile.identity = { nickname: s.nickname ?? "", shirtNumber: s.shirtNumber };

  // Racha de dias: solo cuenta el salto de un dia al siguiente.
  if (profile.lastPlayedDay !== day) {
    const yesterday = dayKeyOf(new Date(now - 86_400_000));
    profile.dayStreak = profile.lastPlayedDay === yesterday ? profile.dayStreak + 1 : 1;
    profile.lastPlayedDay = day;
  }

  profile.winStreak = outcome.result === "win" ? profile.winStreak + 1 : 0;
  profile.bestWinStreak = Math.max(profile.bestWinStreak, profile.winStreak);

  const tot = profile.totals;
  tot.matches += 1;
  if (outcome.result === "win") tot.wins += 1;
  else if (outcome.result === "draw") tot.draws += 1;
  else tot.losses += 1;
  tot.goals += s?.goals ?? 0;
  tot.assists += s?.assists ?? 0;
  tot.shots += s?.shots ?? 0;
  tot.passes += s?.passes ?? 0;
  tot.passesOk += s?.passesOk ?? 0;
  tot.recoveries += s?.recoveries ?? 0;
  tot.touches += s?.touches ?? 0;
  tot.turnovers += s?.losses ?? 0;
  tot.goalsFor += outcome.goalsFor;
  tot.goalsAgainst += outcome.goalsAgainst;
  tot.seconds += Math.max(0, Math.round(outcome.durationSeconds));
  if (outcome.isMvp) tot.mvps += 1;
  if (outcome.cleanSheet) tot.cleanSheets += 1;

  // Semana nueva, tabla nueva.
  if (profile.weekly.weekKey !== week) profile.weekly = emptyWeekly(week);
  if (outcome.online) {
    profile.weekly.points += weeklyPointsFor(outcome);
    profile.weekly.matches += 1;
    profile.weekly.goals += s?.goals ?? 0;
    profile.weekly.assists += s?.assists ?? 0;
    if (outcome.isMvp) profile.weekly.mvps += 1;
  }

  const xpGained = xpForMatch({
    online: outcome.online,
    result: outcome.result,
    goals: s?.goals ?? 0,
    assists: s?.assists ?? 0,
    shots: s?.shots ?? 0,
    passesOk: s?.passesOk ?? 0,
    recoveries: s?.recoveries ?? 0,
    isMvp: outcome.isMvp,
    cleanSheet: outcome.cleanSheet,
  });
  profile.xp += xpGained;
  profile.level = levelFor(profile.xp).level;
  profile.overall = ratePlayer(profile.totals).overall;

  // Records. El orden importa: `overall` ya esta actualizado, asi que "mejor
  // media" se puede batir en el partido que la alcanza.
  const rating = s ? playerScore(s) : 0;
  const rec = profile.records;
  const brokenRecords: string[] = [];
  const beat = (
    key: "goalsInMatch" | "assistsInMatch" | "recoveriesInMatch" | "ratingInMatch" | "biggestWin",
    value: number,
    label: string,
  ): void => {
    // Un record solo se "bate" si ya habia algo que batir: el primer partido no
    // puede disparar nueve avisos de record, seria ruido y no un premio.
    if (value <= rec[key]) return;
    if (rec[key] > 0) brokenRecords.push(label);
    rec[key] = value;
  };
  beat("goalsInMatch", s?.goals ?? 0, "goals");
  beat("assistsInMatch", s?.assists ?? 0, "assists");
  beat("recoveriesInMatch", s?.recoveries ?? 0, "recoveries");
  beat("ratingInMatch", rating, "rating");
  if (outcome.result === "win") beat("biggestWin", outcome.goalsFor - outcome.goalsAgainst, "win");
  if (outcome.firstGoalSecond !== null) {
    rec.fastestGoal =
      rec.fastestGoal === null
        ? outcome.firstGoalSecond
        : Math.min(rec.fastestGoal, outcome.firstGoalSecond);
  }
  rec.bestWeekPoints = Math.max(rec.bestWeekPoints, profile.weekly.points);
  rec.bestDayStreak = Math.max(rec.bestDayStreak, profile.dayStreak);
  rec.bestOverall = Math.max(rec.bestOverall, profile.overall);

  for (const nickname of outcome.teammates) {
    const existing = profile.partners.find((p) => p.nickname === nickname);
    if (existing) {
      existing.played += 1;
      if (outcome.result === "win") existing.won += 1;
      existing.lastAt = now;
    } else {
      profile.partners.push({
        nickname,
        played: 1,
        won: outcome.result === "win" ? 1 : 0,
        lastAt: now,
      });
    }
  }
  profile.partners.sort((a, b) => b.played - a.played || b.lastAt - a.lastAt);
  profile.partners.length = Math.min(profile.partners.length, PARTNER_LIMIT);

  profile.recent.unshift({
    at: now,
    modeId: outcome.modeId,
    online: outcome.online,
    result: outcome.result,
    goalsFor: outcome.goalsFor,
    goalsAgainst: outcome.goalsAgainst,
    goals: s?.goals ?? 0,
    assists: s?.assists ?? 0,
    mvp: outcome.isMvp,
    shots: s?.shots ?? 0,
    passes: s?.passes ?? 0,
    passesOk: s?.passesOk ?? 0,
    recoveries: s?.recoveries ?? 0,
    turnovers: s?.losses ?? 0,
    touches: s?.touches ?? 0,
    rating,
    shirtNumber: s?.shirtNumber ?? profile.identity.shirtNumber,
    xp: xpGained,
    overall: profile.overall,
    seconds: Math.max(0, Math.round(outcome.durationSeconds)),
    ...(outcome.aiLevel !== undefined ? { aiLevel: Math.round(outcome.aiLevel * 100) / 100 } : {}),
  });
  profile.recent.length = Math.min(profile.recent.length, HISTORY_LIMIT);

  // Los trofeos se comprueban con la carrera YA actualizada: "veinticinco
  // partidos" tiene que poder cumplirse en el partido que lo alcanza, y
  // "media de 70" en el que la sube.
  const unlocked: Trophy[] = [];
  for (const trophy of TROPHIES) {
    if (profile.unlocked[trophy.id]) continue;
    if (!trophy.test(outcome, profile.totals, profile)) continue;
    profile.unlocked[trophy.id] = now;
    unlocked.push(trophy);
  }

  const promoted: BadgePromotion[] = [];
  for (const badge of BADGES) {
    const from = tiersBefore.get(badge.id) ?? 0;
    const to = badgeProgress(badge, profile).tier;
    if (to > from) promoted.push({ badge, from, to });
  }

  return {
    profile,
    unlocked,
    promoted,
    xpGained,
    overallBefore,
    overallAfter: profile.overall,
    levelBefore,
    levelAfter: profile.level,
    brokenRecords,
  };
}

/** `structuredClone` no existe en todos los runtimes de prueba. */
function structuredCloneish(p: Profile): Profile {
  return JSON.parse(JSON.stringify(p)) as Profile;
}

// ------------------------------------------------------------ almacenamiento

export interface ProfileStore {
  get(): Profile;
  /** Guarda el partido y devuelve lo que ha cambiado. */
  record(outcome: MatchOutcome, now?: number): ApplyResult;
  /** Sustituye la carrera entera (la que baja del server al recuperar cuenta). */
  adopt(next: Profile): void;
  reset(): void;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): StorageLike | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * Sube una carrera de la v1 a la v2 sin perder nada.
 *
 * La v1 no guardaba pases completados, toques ni perdidas, asi que los tres
 * atributos que dependen de ellos arrancan del promedio de partida en vez de
 * salir a cero: castigar con un 40 de PASE a quien lleva cien partidos por un
 * cambio de formato seria mentir sobre el jugador.
 *
 * La XP se estima con la misma tabla que usa un partido de verdad. No es exacta
 * —faltan los pases y no se sabe cuales fueron online— pero deja al veterano en
 * el nivel que le toca en vez de mandarlo al 1.
 */
export function migrateProfile(old: Partial<Profile>, now = Date.now()): Profile {
  const base = emptyProfile(now);
  const totals: CareerTotals = { ...base.totals, ...old.totals };
  const n = totals.matches;

  if (totals.passes > 0 && totals.passesOk === 0) totals.passesOk = Math.round(totals.passes * 0.62);
  if (totals.touches === 0) totals.touches = Math.round(n * 30);
  if (totals.turnovers === 0) totals.turnovers = Math.round(n * 6);

  const xp =
    n * 20 +
    totals.wins * 60 +
    totals.draws * 30 +
    totals.goals * 40 +
    totals.assists * 25 +
    totals.recoveries * 3 +
    totals.shots * 2 +
    totals.mvps * 75 +
    totals.cleanSheets * 20;

  const oldRecent = Array.isArray(old.recent) ? old.recent : [];
  const records: CareerRecords = { ...base.records, ...old.records };
  for (const m of oldRecent) {
    records.goalsInMatch = Math.max(records.goalsInMatch, m.goals ?? 0);
    records.assistsInMatch = Math.max(records.assistsInMatch, m.assists ?? 0);
    if (m.result === "win") {
      records.biggestWin = Math.max(records.biggestWin, (m.goalsFor ?? 0) - (m.goalsAgainst ?? 0));
    }
  }
  records.bestWeekPoints = Math.max(records.bestWeekPoints, old.weekly?.points ?? 0);
  records.bestDayStreak = Math.max(records.bestDayStreak, old.dayStreak ?? 0);

  const overall = ratePlayer(totals).overall;
  records.bestOverall = Math.max(records.bestOverall, overall);

  return {
    ...base,
    ...old,
    version: PROFILE_VERSION,
    createdAt: old.createdAt ?? now,
    identity: { ...base.identity, ...old.identity },
    xp: typeof old.xp === "number" ? old.xp : xp,
    level: levelFor(typeof old.xp === "number" ? old.xp : xp).level,
    overall,
    totals,
    records,
    weekly: { ...base.weekly, ...old.weekly },
    partners: Array.isArray(old.partners) ? old.partners : [],
    unlocked: old.unlocked ?? {},
    // Los partidos viejos no traen las columnas nuevas: se rellenan con ceros
    // para que el historial no tenga huecos ni `undefined` en pantalla.
    recent: oldRecent.slice(0, HISTORY_LIMIT).map((m) => ({
      ...m,
      shots: m.shots ?? 0,
      passes: m.passes ?? 0,
      passesOk: m.passesOk ?? 0,
      recoveries: m.recoveries ?? 0,
      turnovers: m.turnovers ?? 0,
      touches: m.touches ?? 0,
      rating: m.rating ?? 0,
      shirtNumber: m.shirtNumber ?? base.identity.shirtNumber,
      xp: m.xp ?? 0,
      overall: m.overall ?? 0,
      seconds: m.seconds ?? 0,
    })),
  };
}

export function loadProfile(storage: StorageLike | null = safeStorage(), key = STORAGE_KEY): Profile {
  if (!storage) return emptyProfile();
  try {
    const raw = storage.getItem(key);
    if (!raw) return emptyProfile();
    const parsed = JSON.parse(raw) as Partial<Profile>;
    if (typeof parsed.version !== "number" || parsed.version > PROFILE_VERSION) {
      // De una version del FUTURO no se puede migrar hacia atras (quien juega en
      // dos pestanas con dos versiones), y adivinar romperia la carrera buena.
      return emptyProfile();
    }
    return migrateProfile(parsed);
  } catch {
    return emptyProfile();
  }
}

export function createProfileStore(
  storage: StorageLike | null = safeStorage(),
  side: ProfileSide = "online",
): ProfileStore {
  const key = STORAGE_KEYS[side];
  let profile: Profile = { ...loadProfile(storage, key), side };

  const persist = (): void => {
    profile.side = side;
    try {
      storage?.setItem(key, JSON.stringify(profile));
    } catch {
      // Sin espacio o en modo privado: la partida sigue, la carrera no se guarda.
    }
  };

  return {
    get: () => profile,
    adopt(next) {
      profile = { ...next, side };
      persist();
    },
    record(outcome, now = Date.now()) {
      const applied = applyMatch(profile, outcome, now);
      profile = applied.profile;
      persist();
      return applied;
    },
    reset() {
      profile = { ...emptyProfile(), side };
      try {
        storage?.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

/**
 * A que lado va un perfil de antes de la separacion: al de la mayoria de sus
 * ultimos partidos. Los totales se guardaron mezclados y no se pueden partir
 * con exactitud; lo honrado es mandarlo entero a donde mas se jugo, y que el
 * otro lado empiece de cero.
 */
export function sideOfLegacy(profile: Pick<Profile, "recent">): ProfileSide {
  const recent = Array.isArray(profile.recent) ? profile.recent : [];
  const online = recent.filter((m) => m.online).length;
  // En empate (o sin historial) se queda donde estaba: en el online, que es la
  // clave de siempre. Solo se muda si la mayoria fue contra la IA.
  return recent.length - online > online ? "offline" : "online";
}

/**
 * Parte, una sola vez, el perfil unico de antes en online y offline.
 *
 * El online conserva la clave vieja, asi que si el perfil era sobre todo de
 * partidos online no hay nada que mover. Si era de contra la IA, se muda entero
 * a la clave offline y el online arranca limpio.
 */
export function splitLegacyProfile(storage: StorageLike | null = safeStorage()): void {
  if (!storage) return;
  try {
    if (storage.getItem(SPLIT_FLAG_KEY)) return;
    const raw = storage.getItem(STORAGE_KEYS.online);
    if (raw && !storage.getItem(STORAGE_KEYS.offline)) {
      const parsed = JSON.parse(raw) as Partial<Profile>;
      if (!parsed.side && sideOfLegacy(parsed as Profile) === "offline") {
        storage.setItem(STORAGE_KEYS.offline, JSON.stringify({ ...parsed, side: "offline" }));
        storage.removeItem(STORAGE_KEYS.online);
      }
    }
    storage.setItem(SPLIT_FLAG_KEY, "1");
  } catch {
    // Sin almacenamiento: no hay nada que partir.
  }
}

/** Los dos perfiles. Cada partido va al suyo segun sea online o no. */
export interface ProfileBook {
  online: ProfileStore;
  offline: ProfileStore;
  of(side: ProfileSide): ProfileStore;
  /** El que tiene mas partidos: el que representa al jugador en el menu. */
  main(): Profile;
}

export function createProfileBook(storage: StorageLike | null = safeStorage()): ProfileBook {
  splitLegacyProfile(storage);
  const online = createProfileStore(storage, "online");
  const offline = createProfileStore(storage, "offline");
  return {
    online,
    offline,
    of: (side) => (side === "online" ? online : offline),
    main: () => (online.get().totals.matches >= offline.get().totals.matches ? online.get() : offline.get()),
  };
}
